import React, { useEffect, useState } from 'react';
import WithStandardLayout from '@/layouts/withStandard.layout';
import WithCollectionPageLayout, { useCollectionPage } from '@/layouts/withCollectionPage.layout';
import WithReferralLayout from '@/layouts/withReferral.layout';
import CollectionDetails from '@/components/Collections/Collection/CollectionDetails';
import CollectionLoading from '@/components/Collections/Collection/CollectionLoading';
import initializeIconLibrary from '@/utils/fontAwesomeLibrary';
import { useRouter } from 'next/router';
import 'bootstrap/dist/css/bootstrap.min.css';
import { buildServerSideProps } from '@/utils/server';

initializeIconLibrary();

const CollectionInfoPage = () => {
  const { account, collection, loading, loadCollection, loadedSlugRef, setCollection, setLoading } = useCollectionPage();
  const router = useRouter();
  const { collectionSlug } = router.query;
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const updateCollection = () => loadCollection({ slug: collectionSlug });

  const updateCategories = () => {
    setCategoriesLoading(true);
    loadCollection({ slug: collectionSlug, categoriesOnly: true }).finally(() => setCategoriesLoading(false));
  };

  // Fires when the route changes or the account state changes (e.g. after login).
  // If navigating to a different collection, checks sessionStorage for a cached version
  // to render immediately while the fresh fetch completes in the background.
  // Re-fetches silently (no loading spinner) when the same collection is already loaded,
  // so permission-sensitive data stays current after auth changes.
  useEffect(() => {
    if (!router.isReady || !collectionSlug) return;
    const slug = collectionSlug;
    const isSlugChanged = loadedSlugRef.current !== slug;
    let hasCachedCollection = false;
    if (isSlugChanged) {
      try {
        const cached = sessionStorage.getItem(`col_${slug}`);
        if (cached) {
          setCollection(JSON.parse(cached));
          setLoading(false);
          hasCachedCollection = true;
        }
      } catch (e) {}
    }
    if (isSlugChanged && !hasCachedCollection) setLoading(true);
    loadCollection({ slug }).finally(() => setCategoriesLoading(false));
  }, [router.isReady, router.query, account?.data]);

  return loading ? (
    <CollectionLoading showActionButtons />
  ) : (
    <CollectionDetails
      account={account}
      collection={collection}
      updateCollection={updateCollection}
      updateCategories={updateCategories}
      categoriesLoading={categoriesLoading}
    />
  );
};

export const getServerSideProps = buildServerSideProps();

CollectionInfoPage.getLayout = function getLayout(page) {
  return (
    <WithStandardLayout host={page.props.host}>
      <WithReferralLayout>
        <WithCollectionPageLayout>{page}</WithCollectionPageLayout>
      </WithReferralLayout>
    </WithStandardLayout>
  );
};

export default CollectionInfoPage;
